import { IPropertyPresentable, IPropertyEditable } from "../../../../base/domain/system/edit";
import { TextAlignKinds } from "../../../../base/domain/drawing";
import {
  ExpressionKinds,
  INumberRangeAlertBehavior,
  IDecimalRoundBehavior,
  IExpressionBehavior,
  ICalculationExpressionBehavior,
  IConditionalExpressionBehavior,
  IDateDurationExpressionBehavior,
  ITimeSpanExpressionBehavior,
  IDateComparisonExpressionBehavior,
  INumberStyle,
} from "../../../../documaker/domain/format/objects";
import { IFormatObjectEditable } from "../IFormatObjectEditable";
import { IFormatSpecification } from "../IFormatSpecification";
import { ICalculateObject } from "../objects";
import {
  INumberRangeAlertBehaviorPropertyEditable,
  IDecimalRoundBehaviorPropertyEditable,
  INumberStylePropertyEditable,
} from "../properties";
import { AbstractCalculatableObjectEditor } from "./AbstractCalculatableObjectEditor";

export class CalculateObjectEditor extends AbstractCalculatableObjectEditor<ICalculateObject> implements
INumberStylePropertyEditable,
INumberRangeAlertBehaviorPropertyEditable,
IDecimalRoundBehaviorPropertyEditable {
  constructor(source: ICalculateObject | CalculateObjectEditor, specification?: IFormatSpecification, isNew: boolean = false) {
    super(source, specification, isNew);
    this._textStyle = this.createTextStylePropertyEditor(s => (s.expressionKind !== ExpressionKinds.DateComparison));
    this._fontSizeAutoAdjustBehavior = this.createFontSizeAutoAdjustBehaviorPropertyEditor(s => (s.expressionKind !== ExpressionKinds.DateComparison));
    this._printBehavior = this.createPrintBehaviorPropertyEditor(s => (s.expressionKind !== ExpressionKinds.DateComparison));
    this._editBehavior = this.createEditBehaviorPropertyEditor(s => (s.expressionKind !== ExpressionKinds.DateComparison));
    this._freeInputBehavior = this.createFreeInputBehaviorPropertyEditor(s => (s.expressionKind !== ExpressionKinds.DateComparison));
    this._naturalTextBehavior = this.createNaturalTextBehaviorPropertyEditor(s => (s.expressionKind !== ExpressionKinds.DateComparison));
    this._emptyAlertBehavior = this.createEmptyAlertBehaviorPropertyEditor(s => (s.expressionKind === ExpressionKinds.DateComparison));
    this._lengthAlertBehavior = this.createLengthAlertBehaviorPropertyEditor(s => (s.expressionKind !== ExpressionKinds.DateComparison));
    this._tooltipBehavior = this.createTooltipBehaviorPropertyEditor(s => (s.expressionKind !== ExpressionKinds.DateComparison));
    this._inputRuleBehavior = this.createInputRuleBehaviorPropertyEditor(s => (s.expressionKind === ExpressionKinds.Calculation) || (s.expressionKind === ExpressionKinds.Conditional));
    this._kind = this.createPropertyPresenter(s => s.expressionKind);
    this._calculationExpressionBehavior = this.createExpressionBehaviorPropertyEditor<ICalculationExpressionBehavior>(s => (s.expressionKind === ExpressionKinds.Calculation));
    this._conditionalExpressionBehavior = this.createExpressionBehaviorPropertyEditor<IConditionalExpressionBehavior>(s => (s.expressionKind === ExpressionKinds.Conditional));
    this._dateDurationExpressionBehavior = this.createExpressionBehaviorPropertyEditor<IDateDurationExpressionBehavior>(s => (s.expressionKind === ExpressionKinds.DateDuration));
    this._timeSpanExpressionBehavior = this.createExpressionBehaviorPropertyEditor<ITimeSpanExpressionBehavior>(s => (s.expressionKind === ExpressionKinds.TimeSpan));
    this._dateComparisonExpressionBehavior = this.createExpressionBehaviorPropertyEditor<IDateComparisonExpressionBehavior>(s => (s.expressionKind === ExpressionKinds.DateComparison));
    this._numberStyle = this.createPropertyEditor(
      s => s.numberStyle,
      (s, v) => s.numberStyle = v,
      s => (s.expressionKind === ExpressionKinds.Calculation));
    this._numberRangeAlertBehavior = this.createPropertyEditor(
      s => s.numberRangeAlertBehavior,
      (s, v) => s.numberRangeAlertBehavior = v,
      s => (s.expressionKind === ExpressionKinds.Calculation));
    this._decimalRoundBehavior = this.createPropertyEditor(
        s => s.decimalRoundBehavior,
        (s, v) => s.decimalRoundBehavior = v,
        s => (s.expressionKind === ExpressionKinds.Calculation));
  }

  private _kind: IPropertyPresentable<ExpressionKinds>;
  private _calculationExpressionBehavior: IPropertyEditable<ICalculationExpressionBehavior>;
  private _conditionalExpressionBehavior: IPropertyEditable<IConditionalExpressionBehavior>;
  private _dateDurationExpressionBehavior: IPropertyEditable<IDateDurationExpressionBehavior>;
  private _timeSpanExpressionBehavior: IPropertyEditable<ITimeSpanExpressionBehavior>;
  private _dateComparisonExpressionBehavior: IPropertyEditable<IDateComparisonExpressionBehavior>;
  protected _numberStyle: IPropertyEditable<INumberStyle>;
  protected _numberRangeAlertBehavior: IPropertyEditable<INumberRangeAlertBehavior>;
  protected _decimalRoundBehavior: IPropertyEditable<IDecimalRoundBehavior>;

  private createExpressionBehaviorPropertyEditor<T extends IExpressionBehavior>(isWritable?: (s: ICalculateObject) => boolean): IPropertyEditable<T> {
    return this.createPropertyEditor(
      s => s.expressionBehavior as T,
      (s, v) => s.expressionBehavior = v,
      isWritable ? isWritable : s => true);
  }

  getTypeName(): string {
    switch (this.kind().getValue()) {
      case ExpressionKinds.Calculation:     return "計算式";
      case ExpressionKinds.Conditional:     return "条件式";
      case ExpressionKinds.DateDuration:    return "期間日数";
      case ExpressionKinds.TimeSpan:        return "経過時刻";
      case ExpressionKinds.DateComparison:  return "日付比較";
      default:                              return "不正";
    }
  }

  kind(): IPropertyPresentable<ExpressionKinds> {
    return this._kind;
  }

  calculationExpressionBehavior(): IPropertyPresentable<ICalculationExpressionBehavior> {
    return this._calculationExpressionBehavior;
  }

  conditionalExpressionBehavior(): IPropertyPresentable<IConditionalExpressionBehavior> {
    return this._conditionalExpressionBehavior;
  }

  dateDurationExpressionBehavior(): IPropertyPresentable<IDateDurationExpressionBehavior> {
    return this._dateDurationExpressionBehavior;
  }

  timeSpanExpressionBehavior(): IPropertyPresentable<ITimeSpanExpressionBehavior> {
    return this._timeSpanExpressionBehavior;
  }

  dateComparisonExpressionBehavior(): IPropertyPresentable<IDateComparisonExpressionBehavior> {
    return this._dateComparisonExpressionBehavior;
  }

  numberStyle(): IPropertyEditable<INumberStyle> {
    return this._numberStyle;
  }

  numberRangeAlertBehavior(): IPropertyEditable<INumberRangeAlertBehavior> {
    return this._numberRangeAlertBehavior;
  }

  decimalRoundBehavior(): IPropertyEditable<IDecimalRoundBehavior> {
    return this._decimalRoundBehavior;
  }

  getSelectableAlignmentKinds(): TextAlignKinds[] {
    return this._specification.objects.calculate.alignmentKinds;
  }

  clone(): IFormatObjectEditable {
    return new CalculateObjectEditor(this);
  }
}
